/**
 * El análisis a fondo de las campañas de mail (`GET /emails/analisis`): lo que el Resumen no muestra. Va en el Resumen
 * (todas las del período) y en el detalle de una campaña (sólo ésa).
 *
 * Pedido de Alan del 12/10/2026: «más datos, ser más perfeccionista». Lo más importante va primero: los hallazgos en
 * una frase, y si las aperturas están infladas por lo automático. Después, el detalle por proveedor y por casilla,
 * cuándo y cuánto tardan, el interés que clasifica Snov y las personas más interesadas.
 */
import { AlertTriangle, Info, Sparkles } from "lucide-react";
import { emailsApi } from "../api";
import type { AnalisisEmails as Analisis, FilaSegmento } from "../types";
import { usePedido } from "../hooks/usePedido";
import { useEstadoEmails } from "../contexto";
import { Tarjeta } from "../../wa-oficial/components/Tarjeta";
import { Esqueleto, ErrorTarjeta } from "../../wa-oficial/components/estadisticas/comun";
import { num, plural } from "../../campana-nueva/utils/format";
import { diaMesHora, tasaTexto } from "../logic/emails";
import { DIAS_CORTOS, intensidad, maxDelMapa, textoAutomaticas, textoDemora, totalInteres } from "../logic/analisis";

interface Props {
  rango: { desde?: string; hasta?: string };
  /** sólo esta campaña; sin ella, las del comercio que mandaron en el rango */
  campana?: number;
}

const COLOR_NIVEL = {
  rojo: { caja: "border-red-100 bg-red-50 text-red-800", icono: AlertTriangle },
  ambar: { caja: "border-amber-100 bg-amber-50 text-amber-900", icono: AlertTriangle },
  info: { caja: "border-sky-100 bg-sky-50 text-sky-900", icono: Info },
} as const;

const COLOR_CELDA = ["bg-slate-50", "bg-violet-100", "bg-violet-200", "bg-violet-400", "bg-violet-600"] as const;

function TablaSegmentos({ filas, titulo }: { filas: FilaSegmento[]; titulo: string }) {
  if (!filas.length) return <p className="text-sm text-slate-500">Sin datos todavía.</p>;
  return (
    <div className="overflow-x-auto -mx-2">
      <table className="w-full min-w-[520px] text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-500">
            <th className="px-2 py-1.5 font-medium">{titulo}</th>
            <th className="px-2 py-1.5 font-medium text-right">Contactados</th>
            <th className="px-2 py-1.5 font-medium text-right">Rebote</th>
            <th className="px-2 py-1.5 font-medium text-right">Apertura</th>
            <th className="px-2 py-1.5 font-medium text-right">Clic</th>
            <th className="px-2 py-1.5 font-medium text-right">Respuesta</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={f.clave} className="border-t border-slate-100">
              <td className="px-2 py-2 font-medium text-slate-800 break-all">{f.nombre}</td>
              <td className="px-2 py-2 text-right tabular-nums">{num(f.contactados)}</td>
              <td className={`px-2 py-2 text-right tabular-nums ${(f.tasas.rebote || 0) >= 0.05 && f.contactados >= 30 ? "text-red-700 font-semibold" : ""}`}>{tasaTexto(f.tasas.rebote)}</td>
              <td className="px-2 py-2 text-right tabular-nums">{tasaTexto(f.tasas.apertura)}</td>
              <td className="px-2 py-2 text-right tabular-nums">{tasaTexto(f.tasas.clic)}</td>
              <td className="px-2 py-2 text-right tabular-nums">{tasaTexto(f.tasas.respuesta)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MapaCalor({ mapa }: { mapa: Analisis["mapa"] }) {
  const max = maxDelMapa(mapa);
  if (!max) return <p className="text-sm text-slate-500">Todavía no hay aperturas, clics ni respuestas de personas para mostrar.</p>;
  return (
    <div className="overflow-x-auto -mx-2 px-2">
      <div className="inline-grid gap-[3px] min-w-[620px]" style={{ gridTemplateColumns: "36px repeat(24, minmax(18px, 1fr))" }} role="img"
        aria-label="Mapa de calor de aperturas, clics y respuestas por día y hora">
        <span />
        {Array.from({ length: 24 }, (_x, h) => (
          <span key={h} className="text-[10px] text-slate-400 text-center">{h % 3 === 0 ? h : ""}</span>
        ))}
        {DIAS_CORTOS.map((dia, d) => (
          <div key={dia} className="contents">
            <span className="text-[11px] text-slate-500 pr-1 self-center">{dia}</span>
            {mapa.filter((c) => c.dia === d).map((c) => (
              <span key={c.hora} className={`h-5 rounded-[3px] ${COLOR_CELDA[intensidad(c, max)]}`}
                title={`${dia} ${c.hora}:00 · ${c.envios} ${plural(c.envios, "mail enviado", "mails enviados")} · ${c.aperturas} ${plural(c.aperturas, "apertura", "aperturas")}, ${c.clics} ${plural(c.clics, "clic", "clics")}, ${c.respuestas} ${plural(c.respuestas, "respuesta", "respuestas")}`} />
            ))}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Más oscuro = más interacción. Una respuesta pesa más que un clic, y un clic más que una apertura. Sin lo automático. Pasá el mouse por una celda para ver cuántos mails salieron a esa hora: la gente suele interactuar cerca de cuando recibe.</p>
    </div>
  );
}

export function AnalisisEmails({ rango, campana }: Props) {
  const { version } = useEstadoEmails();
  const r = usePedido<Analisis>(() => emailsApi.analisis({ ...rango, campana }), `${rango.desde}|${rango.hasta}|${campana ?? ""}`, { refresco: version });
  const d = r.dato;

  if (!d) {
    return r.error
      ? <ErrorTarjeta texto={`No pudimos traer el análisis. ${r.error}`} onReintentar={r.recargar} />
      : <Esqueleto alto="h-48" etiqueta="Cargando el análisis" />;
  }
  if (!d.campanas) return null;

  const automaticas = textoAutomaticas(d.automaticas);
  const interes = totalInteres(d.interes);

  return (
    <section aria-labelledby="analisis-a-fondo" className="space-y-6" data-testid="analisis-emails">
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-violet-500" />
        <h3 id="analisis-a-fondo" className="text-base font-semibold text-slate-900">Análisis a fondo</h3>
      </div>

      {d.hallazgos.length > 0 && (
        <ul className="space-y-2" aria-label="Lo que hay que mirar">
          {d.hallazgos.map((h) => {
            const { caja, icono: Icono } = COLOR_NIVEL[h.nivel];
            return (
              <li key={h.texto} className={`rounded-xl border px-4 py-3 text-sm flex items-start gap-2 ${caja}`}>
                <Icono size={15} className="flex-shrink-0 mt-0.5" /> <span>{h.texto}</span>
              </li>
            );
          })}
        </ul>
      )}

      {automaticas && (
        <p className="text-sm text-slate-600" data-testid="analisis-automaticas">
          <strong className="text-slate-800">Aperturas reales:</strong> {automaticas}
          {d.automaticas.clics > 0 && ` Tampoco ${plural(d.automaticas.clics, "cuenta", "cuentan")} ${num(d.automaticas.clics)} ${plural(d.automaticas.clics, "clic automático", "clics automáticos")}.`}
        </p>
      )}

      {/* una debajo de la otra: lado a lado, en una pantalla mediana, las columnas de las tasas quedaban cortadas */}
      <Tarjeta titulo="Por proveedor de correo" sub="Si en uno casi nadie abre, ahí el mail cae en spam; si rebota mucho, hay direcciones viejas">
        <TablaSegmentos filas={d.proveedores} titulo="Proveedor" />
      </Tarjeta>
      <Tarjeta titulo="Por casilla que envía" sub="Cuál rebota o rinde menos">
        <TablaSegmentos filas={d.remitentes} titulo="Casilla" />
      </Tarjeta>

      <Tarjeta titulo="Cuándo interactúa tu gente" sub="Por día de la semana y hora (de Buenos Aires)">
        <MapaCalor mapa={d.mapa} />
      </Tarjeta>

      <div className="grid gap-6 lg:grid-cols-3">
        {([
          ["apertura", "Hasta que abre"],
          ["clic", "Hasta el primer clic"],
          ["respuesta", "Hasta que contesta"],
        ] as const).map(([k, titulo]) => {
          const dem = d.demoras[k];
          return (
            <Tarjeta key={k} titulo={titulo} sub={dem.personas ? `La mitad, en menos de ${textoDemora(dem.medianaMin)}` : "Todavía nadie"}>
              {dem.personas > 0 && (
                <ul className="space-y-2.5">
                  {dem.tramos.map((t) => {
                    const pct = Math.round((t.personas / dem.personas) * 100);
                    return (
                      <li key={t.clave}>
                        <div className="flex items-baseline justify-between gap-2 text-xs">
                          <span className="text-slate-600">{t.texto}</span>
                          <span className="tabular-nums text-slate-500">{num(t.personas)} · {pct} %</span>
                        </div>
                        <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full bg-violet-400" style={{ width: `${pct}%` }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Tarjeta>
          );
        })}
      </div>

      {d.interes && interes > 0 && (
        <Tarjeta titulo="Qué dicen las respuestas" sub="Como las clasifica Snov">
          <div className="grid grid-cols-3 gap-3 text-center">
            {([
              ["Interesados", d.interes.interesados, "text-emerald-700"],
              ["Tal vez", d.interes.quizas, "text-amber-700"],
              ["No interesados", d.interes.noInteresados, "text-slate-600"],
            ] as const).map(([t, n, cls]) => (
              <div key={t} className="rounded-xl bg-slate-50 px-3 py-3">
                <p className={`text-2xl font-bold tabular-nums ${cls}`}>{num(n)}</p>
                <p className="text-xs text-slate-500">{t}</p>
              </div>
            ))}
          </div>
        </Tarjeta>
      )}

      <Tarjeta titulo="Las personas más interesadas" sub="Contestar vale 5, cada clic 3 y cada apertura 1 (hasta 5). Sin bajas, rebotes ni lo automático">
        {d.interesadas.length === 0 ? (
          <p className="text-sm text-slate-500">Todavía nadie mostró interés suficiente.</p>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full min-w-[620px] text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500">
                  <th className="px-2 py-1.5 font-medium">Persona</th>
                  <th className="px-2 py-1.5 font-medium">Teléfono</th>
                  <th className="px-2 py-1.5 font-medium text-right">Aperturas</th>
                  <th className="px-2 py-1.5 font-medium text-right">Clics</th>
                  <th className="px-2 py-1.5 font-medium text-right">Respuestas</th>
                  <th className="px-2 py-1.5 font-medium text-right">Puntaje</th>
                  <th className="px-2 py-1.5 font-medium">Última vez</th>
                </tr>
              </thead>
              <tbody>
                {d.interesadas.map((p) => (
                  <tr key={p.email} className="border-t border-slate-100">
                    <td className="px-2 py-2">
                      <span className="block font-medium text-slate-800">{p.nombre || "Sin nombre"}</span>
                      <span className="block text-xs text-slate-500 break-all">{p.email}</span>
                    </td>
                    <td className="px-2 py-2 text-slate-600 whitespace-nowrap">{p.telefono || "—"}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{num(p.aperturas)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{num(p.clics)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{num(p.respuestas)}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-semibold text-violet-700">{num(p.puntaje)}</td>
                    <td className="px-2 py-2 text-xs text-slate-500 whitespace-nowrap">{diaMesHora(p.ultimaInteraccion)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>
    </section>
  );
}
